<template>
  <div>
    <div class="d-flex align-items-center justify-content-end mt-3 mb-4">
      <!-- 匯出 Excel -->
      <div>
        <b-button
          variant="inverse-primary"
          class="ml-0 ml-xl-4"
          @click="handleExport"
        >
          <span class="mdi mdi-file-excel mr-1"></span>
          匯出 Excel
        </b-button>
      </div>

      <b-button
        v-b-toggle.collapse-1-inner
        variant="link"
        class="flex-shrink-0 ml-0 ml-xl-1"
      >
        進階搜尋<i class="fa fa-chevron-down"></i>
      </b-button>
    </div>

    <b-collapse id="collapse-1-inner" class="mt-2">
      <div class="border p-2">
        <b-form class="">
          <div class="d-flex flex-column flex-xl-row flex-wrap">
            <b-form-group
              label="填寫內容"
              label-cols-sm="3"
              label-cols-lg="2"
              content-cols-sm
              content-cols-lg="7"
              style="width: 50%"
            >
              <b-form-input
                class="mr-3"
                v-model="query.res_keyword"
                placeholder="搜尋填寫內容"
                :disabled="loading"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="會員名稱"
              label-cols-sm="3"
              label-cols-lg="2"
              content-cols-sm
              content-cols-lg="7"
              style="width: 50%"
            >
              <b-form-input
                class="mr-3"
                v-model="query.customer_name"
                placeholder="搜尋會員名稱"
                :disabled="loading"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="會員編號"
              label-cols-sm="3"
              label-cols-lg="2"
              content-cols-sm
              content-cols-lg="7"
              style="width: 50%"
            >
              <b-form-input
                class="mr-3"
                v-model="query.customer_outer_code"
                placeholder="搜尋會員編號"
                :disabled="loading"
              ></b-form-input>
            </b-form-group>
            <!-- Dynamic customer profile fields -->
            <b-form-group
              v-for="field in dynamicCustomerFields"
              :key="field.key"
              :label="field.label"
              label-cols-sm="3"
              label-cols-lg="2"
              content-cols-sm
              content-cols-lg="7"
              style="width: 50%"
            >
              <b-form-input
                class="mr-3"
                v-model="query[field.queryKey]"
                :placeholder="`搜尋${field.label}`"
                :disabled="loading"
              ></b-form-input>
            </b-form-group>
          </div>

          <div class="flex-column">
            <b-button
              variant="primary"
              class="mb-2 mb-xl-0 mr-2"
              @click="resetSearch"
            >
              <i class="mdi mdi-restart"></i> 重置搜尋條件
            </b-button>
            <b-button
              variant="primary"
              class="mb-2 mb-xl-0"
              @click="handlePageReset"
            >
              <i class="fa fa-search"></i> 搜尋
            </b-button>
          </div>
        </b-form>
      </div>
    </b-collapse>

    <b-table
      responsive
      striped
      hover
      :items="results"
      :fields="fields"
      @sort-changed="handleSort"
      :busy="loading"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>
      <template #cell(customer_name)="data">
        <TableCell
          :content="data.item.customer?.name ?? ''"
          :title="fields.find(f => f.key === 'customer_name').label"
        />
      </template>
      <template #cell(outer_code)="data">
        <TableCell
          :content="data.item.customer?.outer_code ?? ''"
          :title="fields.find(f => f.key === 'outer_code').label"
        />
      </template>
      <template #cell()="data">
        <TableCell
          v-if="data.value !== undefined"
          :content="data.value"
          :title="fields.find(f => f.key === data.field.key).label"
        />
      </template>

      <!-- <template #cell(arrange)="data">
        <b-button
          class="ml-2"
          variant="inverse-primary"
          v-if="checkPermission([consts.INVOICE_LIST_VIEW])"
          :to="{
            name: 'InvoiceDetail',
            params: {
              invoiceId: data.item.id,
            },
          }"
          ><span class="mdi mdi-eye mr-1"></span>查看
        </b-button>
      </template> -->
      <template #cell(created_at)="data">
        {{ formatDate(data.item.created_at) }}
      </template>
      <template #cell(actions)="data">
        <b-button
          v-if="checkPermission([consts.FORM_RESULT_DELETE])"
          variant="inverse-danger"
          @click="deleteResult(data.item)"
        >
          <span class="mdi mdi-delete"></span> 刪除
        </b-button>
      </template>
    </b-table>
    <CustomPagination
      :currentPage="query.page"
      :totalRows="totalRows"
      :perPage="query.per_page"
      @page-change="handlePageChange"
      @per-page-change="handlePerPageChange"
    />
  </div>
</template>

<script>
import formApi from "@/apis/form";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import deepGet from "lodash/get";
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import TableCell from "@/components/Dashboard/FormResult/TableCell.vue";

const initialQuery = {
  page: 1,
  per_page: 10,
  res_keyword: "",
  customer_name: "",
  customer_outer_code: "",
  sort_by: "created_at",
  order_by: "desc",
};

export default {
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.init();
        }
      }
    }
  },
  components: {
    TableCell,
    CustomPagination,
  },
  data() {
    return {
      consts: consts,
      totalRows: 0,
      loading: false,
      fields: [],
      defaultFields: [
        {
          key: "customer_name",
          label: "顯示名稱",
          sortable: true,
        },
        {
          key: "outer_code",
          label: "識別編號",
          sortable: true,
        },
      ],
      results: [],
      query: { ...initialQuery },
      form: {
        title: null,
      },
    }
  },
  computed: {
    ...mapGetters('dashboardModule', ["getModule"]),
    ...mapState('general', ['organization']),
    dynamicCustomerFields() {
      return this.fields
        .filter(field => field.key && field.key.startsWith('customer.profiles.'))
        .map(field => ({
          key: field.key,
          label: field.label,
          queryKey: `customer_profile_${field.key.replace('customer.profiles.', '')}`
        }));
    },
  },
  methods: {
    ...mapActions('dashboardModule', ['fetchModule']),
    async fetchResults () {
      try {
        const response = await formApi.getResults(this.$route.params.id, this.query);
        this.results = response.data.data;
        this.totalRows = response.data.meta.total;
        this.form = response.data.form;

        this.fields = [...this.defaultFields];

        // 插入在 config 額外設定的 customer 相關欄位
        await this.fetchModule({ code: 'customer', org_id: this.organization.id })
        const profileMappings = deepGet(this.getModule("customer"), 'module_config.survey.customer_list', [])
          .find(fieldMappingConfig => fieldMappingConfig.merchant_type === consts.MERCHANT_TYPES.LINE_TYPE)
        this.fields = [
          ...this.fields.slice(0, 2),
          ...deepGet(profileMappings, 'member_fields', [])
            .map((mapping) => {
              if (mapping.matched_key.startsWith("extra.")) {
                const key = mapping.matched_key.split("extra.")[1];
                return {
                  key: `customer.profiles.${key}`,
                  label: mapping.title,
                };
              } else {
                return {
                  key: `customer.${mapping.matched_key}`,
                  label: mapping.title,
                };
              }
            }),
          ...this.fields.slice(2)
        ]

        this.fields = this.fields.concat(
          this.form.subjects.map((subject) => {
            return {
              key: subject.id,
              label: subject.config.title ?? '無標題',
            };
          })
        );

        this.fields.push({
          key: "created_at",
          label: "新增時間",
          sortable: true,
        });

        if (this.checkPermission([consts.FORM_RESULT_DELETE])) {
          this.fields.push({
            key: "actions",
            label: "操作",
          });
        }

        // 把 results.responses 依照 subjects 攤開
        for (let i = 0; i < this.results.length; i ++) {
          let result = this.results[i];
          for (let j = 0; j < result.responses.length; j ++) {
            let response = result.responses[j];
            // 如果 response.value 是陣列，就轉成字串
            if (Array.isArray(response.value)) {
              response.value = response.value.join(', ');
            }
            this.results[i][response.subject_id] = response.value;
          }
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取表單結果錯誤", "error");
      }
    },
    async init() {
      this.loading = true
      this.query = updateQueryFromRoute(
        this.$route,
        this.$store,
        this.query,
      );
      await this.fetchResults();
      this.loading = false
    },
    async reset() {
      this.loading = true
      this.$set(this, 'query', { ...initialQuery });
      await this.fetchResults();
      this.loading = false
    },
    async runQueryAndUpdateUrl() {
      this.loading = true
      updateUrlWithoutReload(this.query, this.$store, this.$router);
      await this.fetchResults();
      this.loading = false
    },
    formatDate(value) {
      if (value) {
        return moment(String(value)).format("YYYY-MM-DD HH:mm");
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handlePageChange(newPage) {
      this.query.page = newPage;
      this.runQueryAndUpdateUrl();
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
      this.runQueryAndUpdateUrl();
    },
    async handlePageReset() {
      this.loading = true
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }

      await this.fetchResults();
      this.loading = false
    },
    async resetSearch() {
      this.loading = true;
      // Reset all search fields but preserve pagination settings
      const resetQuery = {
        ...initialQuery,
        per_page: this.query.per_page
      };
      // Reset dynamic customer profile fields
      this.dynamicCustomerFields.forEach(field => {
        resetQuery[field.queryKey] = "";
      });
      this.$set(this, 'query', resetQuery);
      await this.fetchResults();
      this.loading = false;
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async deleteResult(result) {
      const confirmResult = await this.$swal.fire({
        type: "warning",
        title: "確定要刪除這筆填寫資料嗎？",
        text: `填寫者：${result.customer?.name ?? '未知'}`,
        confirmButtonText: "確定刪除",
        cancelButtonText: "取消",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        reverseButtons: true,
      });
      if (confirmResult.value) {
        try {
          await formApi.deleteResult(this.$route.params.id, result.id);
          this.$swal.fire({
            title: "成功",
            type: "success",
            text: "已刪除該筆填寫資料",
          });
          await this.fetchResults();
        } catch (error) {
          this.$swal("失敗", error.response?.data?.message ?? "刪除失敗", "error");
        }
      }
    },
    handleExport() {
      formApi.exportResults(this.form.id, this.query).then(() => {
        this.$swal({
          title: "成功",
          text: "可至“我的主控台”查看紀錄及下載檔案",
          showCancelButton: true,
          cancelButtonText: '確認',
          type: "success",
          confirmButtonText: "前往查看",
        }).then((result) => {
          if (result.value) {
            this.$router.push({
              name: "MyConsoleDownloadFileList",
              params: { org_code: this.$route.params.org_code },
            });
          }
        });
      });
    },
  },
}
</script>
